import React, { useState, useRef, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import MidiPlayer from './MidiPlayer';
import { supabase } from '../lib/supabase';
import { Button } from "./ui/button"
import { Input } from "./ui/input"
import { ScrollArea } from "./ui/scroll-area"
import { Send, RefreshCw, Loader2, User, Bot, Undo, ChevronLeft, ChevronRight, Upload, X, Zap, Sparkles, Lock, ThumbsUp, ThumbsDown } from 'lucide-react';
import LoadingSpinner from './LoadingSpinner';
import OutOfCreditsDialog from './OutOfCreditsDialog';
import EmptyState from './EmptyState';
import { callFunction } from '../lib/functionCaller';
import { logEvent } from '../lib/amplitude';
import ClearHistoryDialog from './ClearHistoryDialog';
import UndoDialog from './UndoDialog';
import WallFeedback, { wallFeedbackAsked, markWallFeedbackAsked } from './WallFeedback';
import { useNavigate } from 'react-router-dom';

const MODE_STORAGE_KEY = 'midimate_generation_mode';


const MAX_RETRIES = 3;

// Status shown while a generation runs, keyed by seconds elapsed. Pro arrangements take
// 15-45s, so the copy needs to keep moving or people assume it has hung.
const LOADING_STAGES = [
  { after: 0, text: 'Reading your prompt…' },
  { after: 3, text: 'Composing the arrangement…' },
  { after: 9, text: 'Writing drums, bass, chords and lead…' },
  { after: 18, text: 'Rendering the MIDI file…' },
  { after: 30, text: 'Almost there — full arrangements take a little longer…' },
  { after: 50, text: 'Still working, hang on…' },
];

const loadingStatusFor = (elapsed) => {
  const stage = [...LOADING_STAGES].reverse().find((item) => elapsed >= item.after);
  return stage ? stage.text : LOADING_STAGES[0].text;
};

const fetchChatHistory = async (userId) => {
  try {
    const { data: chatHistory, error: chatError } = await supabase
      .from('chat_history')
      .select(`
        *,
        midi_files (
          file_path,
          description,
          lilypond_code,
          suggestions
        )
      `)
      .eq('user_id', userId)
      .eq('chat_type', 'midi')  // Add this line
      .order('created_at', { ascending: true });

    if (chatError) throw chatError;

    const combinedHistory = chatHistory.map(message => ({
      ...message,
      role: message.role,
      content: typeof message.message === 'string' ? message.message : JSON.stringify(message.message),
      midiAttachment: message.midi_attachment_name,
      filePath: message.midi_files?.file_path || null,
      description: message.midi_files?.description || null,
      lilypond_code: message.midi_files?.lilypond_code || null,
      suggestions: message.midi_files?.suggestions ? JSON.parse(message.midi_files.suggestions) : []
    }));

    return combinedHistory;
  } catch (error) {
    console.error('Error fetching chat history');
    return [];
  }
};

function Chat({ credits, updateCredits, onPurchaseSubscription, onTopUpPurchase, isSubscriptionActive, userPlan, trialEligible }) {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  // Pro mode is included in the free trial and the Musician and Producer plans; Basic uses Fast
  const canUsePro = !(isSubscriptionActive && (userPlan || 'Basic') === 'Basic');
  const [preferredMode, setPreferredMode] = useState(() => {
    try {
      return localStorage.getItem(MODE_STORAGE_KEY) || 'pro';
    } catch (e) {
      return 'pro';
    }
  });
  const mode = canUsePro ? preferredMode : 'fast';

  const handleModeChange = (newMode) => {
    if (newMode === 'pro' && !canUsePro) {
      logEvent('Pro Mode Upgrade Clicked', { currentPlan: userPlan || 'none' });
      navigate('/pricing');
      return;
    }
    setPreferredMode(newMode);
    try {
      localStorage.setItem(MODE_STORAGE_KEY, newMode);
    } catch (e) {
      // Ignore storage errors; the choice just won't persist
    }
    logEvent('Generation Mode Changed', { mode: newMode });
  };
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentSuggestions, setCurrentSuggestions] = useState([]);
  const [currentLilyPondCode, setCurrentLilyPondCode] = useState('');
  const scrollAreaRef = useRef(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [isClearing, setIsClearing] = useState(false);
  const [isOutOfCreditsDialogOpen, setIsOutOfCreditsDialogOpen] = useState(false);
  const [loadingElapsed, setLoadingElapsed] = useState(0);
  // Offered once, right after the first download, when the user has just seen the value
  const POST_DOWNLOAD_OFFER_KEY = 'midimate_post_download_offer_shown';
  // The one-off pack (create-topup-session / stripe-webhook only accept this size)
  const TOPUP_TOKENS = 50;
  const TOPUP_PRICE = '4.99';
  const [showPostDownloadOffer, setShowPostDownloadOffer] = useState(false);
  const [showDownloadWall, setShowDownloadWall] = useState(false);
  const [showWallFeedback, setShowWallFeedback] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [isWaitingForNewSuggestions, setIsWaitingForNewSuggestions] = useState(false);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const [isClearConfirmOpen, setIsClearConfirmOpen] = useState(false);
  const [isInputFlashing, setIsInputFlashing] = useState(false);
  const [loadingIntervalId, setLoadingIntervalId] = useState(null);
  const [previousLilyPondCode, setPreviousLilyPondCode] = useState('');
  const MAX_CHARS = 1000; // Set the maximum character limit
  const [isUndoDialogOpen, setIsUndoDialogOpen] = useState(false);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);
  const [attachedMidiFile, setAttachedMidiFile] = useState(null);
  const hasInitialized = useRef(false);

  const initialSuggestions = [
    "Make a lo-fi hip hop beat with jazzy piano chords",
    "Afro house groove with percussion, bass and Rhodes chords",
    "Dark trap beat in F minor with an 808 bassline",
    "Catchy pop chord progression with a singable melody",
    "Somber chord progression with lush 9ths and a borrowed iv",
    "Emotional piano chords in D minor",
  ];

  // Add this new function to check if there's any history to clear
  const hasHistoryToClear = messages.length > 0;

  // Add this helper function to count assistant messages
  const countAssistantMessages = (messages) => {
    return messages.filter(message => message.role === 'assistant').length;
  };

  useEffect(() => {
    const fetchSession = async () => {
      if (user && !hasInitialized.current) {
        hasInitialized.current = true;
        setIsLoadingHistory(true);
        try {
          const history = await fetchChatHistory(user.id);
          setMessages(history);
          if (history.length > 0) {
            const lastMessage = history[history.length - 1];
            setCurrentLilyPondCode(lastMessage.lilypond_code || '');
            setPreviousLilyPondCode('');
            
            // Only include "Undo" if there's more than one assistant message
            const suggestions = lastMessage.suggestions || initialSuggestions;
            if (countAssistantMessages(history) > 1) {
              setCurrentSuggestions(["Undo", ...suggestions.slice(0, 5)]);
            } else {
              setCurrentSuggestions(suggestions.slice(0, 5));
            }
          } else {
            setCurrentSuggestions(initialSuggestions);
          }

          logEvent('Chat Session Started', { 
            hasExistingHistory: history.length > 0
          });
        } catch (error) {
          console.error('Error fetching chat history');
          setCurrentSuggestions(initialSuggestions);
        } finally {
          setIsLoadingHistory(false);
        }
      } else if (!user) {
        setCurrentSuggestions(initialSuggestions);
        setIsLoadingHistory(false);
      }
    };

    fetchSession();

    // ... rest of the useEffect
  }, [user]);

  // A prompt typed on the landing page before sign-up is waiting in storage: put it in the
  // box, ready to send, so their first result is their own idea rather than a chip
  useEffect(() => {
    if (isLoadingHistory) return;
    let pending = null;
    try {
      pending = localStorage.getItem('midimate_pending_prompt');
      if (pending) localStorage.removeItem('midimate_pending_prompt');
    } catch (e) {
      return;
    }
    if (!pending) return;
    setInput(pending);
    logEvent('Landing Prompt Prefilled', { prompt: pending });
    setTimeout(() => inputRef.current?.focus(), 300);
  }, [isLoadingHistory]);

  useEffect(() => {
    const handleResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const displayedSuggestions = windowWidth < 640 ? currentSuggestions.slice(0, 3) : currentSuggestions;
  // The first generated result gets a one-line hint about downloading and iterating
  const firstResultIndex = messages.findIndex((m) => m.role === 'assistant' && m.filePath);

  const scrollToBottom = () => {
    if (scrollAreaRef.current) {
      const scrollContainer = scrollAreaRef.current.querySelector('[data-radix-scroll-area-viewport]');
      if (scrollContainer) {
        scrollContainer.scrollTop = scrollContainer.scrollHeight;
      }
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);


  const generateMidi = async (inputText, midiAttachment = null, retryCount = 0) => {
    if (retryCount === 0) {
      setIsLoading(true);
      const startedAt = Date.now();
      setLoadingElapsed(0);
      const newIntervalId = setInterval(() => {
        setLoadingElapsed(Math.floor((Date.now() - startedAt) / 1000));
      }, 1000);
      setLoadingIntervalId(newIntervalId);
      const userMessage = { 
        role: 'user', 
        content: inputText,
        midiAttachment: midiAttachment ? midiAttachment.name : null
      };
      setMessages(prev => [...prev, userMessage]);
      setPreviousLilyPondCode(currentLilyPondCode);
    }

    try {
      if (!user) throw new Error('No active session');

      const { data: { session } } = await supabase.auth.getSession();
      if (!session) throw new Error('No active session');

      let data;
   
      const invokeData = await callFunction('generate-midi', {
        messages: [...messages, { 
          role: 'user', 
          content: inputText,
          midiAttachment: midiAttachment ? midiAttachment.name : null
        }],
        currentLilyPondCode,
        previousLilyPondCode,
        mode,
        midiAttachment: midiAttachment ? midiAttachment.base64 : null
      });
      
      data = invokeData;

      logEvent('MIDI Generated', { 
        mode: data.mode,
        messageCount: messages.length,
        lilyPondCodeLength: data.lilypondCode.length,
      });

      const botMessage = { 
        role: 'assistant', 
        content: data.description,
        midiData: new Uint8Array(data.midiData),
        description: data.description,
        filePath: data.filePath
      };

      setMessages(prev => [...prev, botMessage]);
      setCurrentLilyPondCode(data.lilypondCode);
      setCurrentSuggestions(["Undo", ...(data.suggestions || initialSuggestions)]);
      setIsWaitingForNewSuggestions(false);
      setIsLoading(false);

      // On success, clear the interval and reset loading state
      clearInterval(loadingIntervalId);
      setLoadingIntervalId(null);
      setIsLoading(false);

      return { newCreditBalance: data.newCreditBalance };
    } catch (error) {
      console.error('Error generating MIDI');
      if (retryCount < MAX_RETRIES) {
        return generateMidi(inputText, midiAttachment, retryCount + 1);
      } else {
        // On final failure, clear the interval and reset loading state
        clearInterval(loadingIntervalId);
        setLoadingIntervalId(null);
        setIsLoading(false);

        const errorMessage = { 
          role: 'assistant', 
          content: "I'm sorry, but I encountered an issue while generating the MIDI. Please try again with a different request or rephrase your input.",
          isError: true
        };
        logEvent('MIDI Generation Error', { error: error.message });
        setMessages(prev => [...prev, errorMessage]);
        setIsWaitingForNewSuggestions(false);
      }
    }
  };

  const handleInputChange = (e) => {
    const inputText = e.target.value;
    if (inputText.length <= MAX_CHARS) {
      setInput(inputText);
    }
  };

  const handleSubmit = async (e, suggestedInput = null) => {
    console.log("handleSubmit called");
    if (e) e.preventDefault();
    const inputText = suggestedInput || input.trim();
    
    if (!inputText && !attachedMidiFile) return;
    
    logEvent('Chat Message Sent', { 
      messageLength: inputText.length, 
      message: inputText,
      hasMidiAttachment: !!attachedMidiFile 
    });

    if (!user) {
      // Handle not logged in case
      return;
    }
    if (credits < 1) {
      setIsOutOfCreditsDialogOpen(true);
      return;
    }

    setIsLoading(true);
    try {
      const result = await generateMidi(inputText, attachedMidiFile);
      updateCredits(result.newCreditBalance);
      setInput('');
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
      setAttachedMidiFile(null);
    } catch (error) {
      console.error('Error sending message:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClearHistory = async () => {
    if (!user) return;

    setIsClearing(true);
    setIsClearConfirmOpen(false);
    try {
      // Clear chat history from Supabase
      const { data: deletedChatHistory, error: chatError } = await supabase
        .from('chat_history')
        .delete()
        .eq('user_id', user.id)
        .eq('chat_type', 'midi');

      if (chatError) {
        console.error('Error clearing chat history');
        throw chatError;
      }

      // Clear generated MIDI files from storage
      const { data: midiFiles, error: listError } = await supabase
        .storage
        .from('midi-files')
        .list(user.id);

      // Clear uploaded MIDI files from storage
      const { data: uploadedMidiFiles, error: listUploadedError } = await supabase
        .storage
        .from('midi-files')
        .list(`${user.id}/uploads`);

      if (listError) {
        console.error('Error listing generated MIDI files');
        throw listError;
      }

      if (listUploadedError) {
        console.error('Error listing uploaded MIDI files');
        throw listUploadedError;
      }

      // Combine both generated and uploaded files for deletion
      const filesToRemove = [
        ...(midiFiles?.map(file => `${user.id}/${file.name}`) || []),
        ...(uploadedMidiFiles?.map(file => `${user.id}/uploads/${file.name}`) || [])
      ];

      if (filesToRemove.length > 0) {
        const { data: deletedFiles, error: deleteError } = await supabase
          .storage
          .from('midi-files')
          .remove(filesToRemove);

        if (deleteError) {
          console.error('Error deleting MIDI files');
          throw deleteError;
        }
      }

      // Clear MIDI file metadata from Supabase
      const { data: deletedMidiMetadata, error: midiMetadataError } = await supabase
        .from('midi_files')
        .delete()
        .eq('user_id', user.id);

      if (midiMetadataError) {
        console.error('Error clearing MIDI metadata');
        throw midiMetadataError;
      }

      // Clear frontend state
      setMessages([]);
      setCurrentLilyPondCode('');
      setCurrentSuggestions(initialSuggestions);

      logEvent('MIDI Chat History Cleared', { 
        messageCount: messages.length,
        midiFileCount: (midiFiles?.length || 0) + (uploadedMidiFiles?.length || 0)
      });

    } catch (error) {
      console.error('Error clearing chat history and MIDI files');
    } finally {
      setIsClearing(false);
    }
  };

  const handleSuggestionClick = async (suggestion) => {
    if (credits < 1) {
      setIsOutOfCreditsDialogOpen(true);
      return;
    }

    if (suggestion === "Undo") {
      setIsUndoDialogOpen(true);
    } else {
      if (messages.length === 0) {
        setIsWaitingForNewSuggestions(true);
        setCurrentSuggestions([]); // Clear current suggestions
        await handleSubmit(null, suggestion);
        setIsWaitingForNewSuggestions(false);
      } else {
        setInput(suggestion);
        setIsInputFlashing(true);
        setTimeout(() => setIsInputFlashing(false), 300);
      }
    }

    if (messages.length > 0) {
      const inputElement = document.querySelector('input[type="text"]');
      if (inputElement) {
        inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }

    logEvent('Suggestion Clicked', { 
      suggestion,
      isInitialSuggestion: messages.length === 0,
      isUndo: suggestion === "Undo"
    });
  };

  // Downloads do not mean a result was good (producers audition in their DAW), so each
  // result gets a thumbs up/down. A thumbs down asks why and drafts the redo request.
  const [ratings, setRatings] = useState({});
  const DOWN_REASONS = [
    ['style', 'Wrong style', 'it is not the style I asked for'],
    ['parts', 'Wrong parts', 'it has parts I did not ask for'],
    ['busy', 'Too busy', 'it is too busy'],
    ['simple', 'Too simple', 'it is too plain and repetitive'],
    ['bad', 'Just not good', 'it does not sound good'],
  ];
  const rateResult = (index, rating, reason) => {
    const message = messages[index];
    const prompt = messages.slice(0, index).reverse().find((m) => m.role === 'user')?.content;
    setRatings((r) => ({ ...r, [index]: { rating, reason } }));
    logEvent('Result Rated', { rating, reason, filePath: message?.filePath, prompt, resultIndex: index });
    if (rating === 'down' && reason) {
      const why = DOWN_REASONS.find(([key]) => key === reason)?.[2];
      setInput(`Redo it completely differently: ${why}.`);
      inputRef.current?.focus();
    }
  };

  // A download is the one moment we know the result was worth keeping, so the offer to
  // keep going lives here rather than behind the credits wall. Once per browser.
  const handleDownloaded = () => {
    if (isSubscriptionActive) return;
    try {
      if (localStorage.getItem(POST_DOWNLOAD_OFFER_KEY)) return;
      localStorage.setItem(POST_DOWNLOAD_OFFER_KEY, '1');
    } catch (e) {
      // Storage unavailable: show it anyway, worst case it repeats
    }
    logEvent('Post Download Trial Offer Shown', { credits, trialEligible });
    setShowPostDownloadOffer(true);
  };

  const handlePostDownloadOffer = async (action) => {
    logEvent('Post Download Trial Offer Action', { action, credits });
    setShowPostDownloadOffer(false);
    if (action === 'start_trial') {
      try {
        await onPurchaseSubscription('Musician', 'post_download_offer');
      } catch (error) {
        navigate('/pricing');
      }
    } else if (action === 'top_up') {
      onTopUpPurchase(TOPUP_TOKENS, 'post_download_offer');
    }
  };

  // The first download is free; after that the server says no until a pack or plan is bought.
  // Fails open: a hiccup in the check must never cost someone a file they were allowed to take.
  const claimDownload = async () => {
    try {
      const { data, error } = await supabase.rpc('claim_download');
      if (error) throw error;
      if (data?.allowed) return true;
      logEvent('Download Blocked', { credits, downloads: data?.downloads, trialEligible });
      setShowPostDownloadOffer(false);
      setShowDownloadWall(true);
      return false;
    } catch (error) {
      console.error('claim_download', error);
      return true;
    }
  };

  const handleDownloadWall = async (action) => {
    logEvent('Download Wall Action', { action, credits });
    setShowDownloadWall(false);
    if (action === 'dismiss' && user && !wallFeedbackAsked(user.id)) {
      // Once per account: ask why, in place of the wall, after they have said no
      markWallFeedbackAsked(user.id);
      setShowWallFeedback(true);
    }
    if (action === 'start_trial') {
      try {
        await onPurchaseSubscription('Musician', 'download_wall');
      } catch (error) {
        navigate('/pricing');
      }
    } else if (action === 'top_up') {
      onTopUpPurchase(TOPUP_TOKENS, 'download_wall');
    }
  };

  const handlePurchaseCredits = async (planName) => {
    // Keep the dialog up until checkout opens so the button can show progress
    await onPurchaseSubscription(planName, 'out_of_credits');
    setIsOutOfCreditsDialogOpen(false);
  };

  const handleTopUpPurchase = (amount) => {
    setIsOutOfCreditsDialogOpen(false);
    onTopUpPurchase(amount, 'out_of_credits');
  };

  const handleOutOfCreditsDialogClose = () => {
    setIsOutOfCreditsDialogOpen(false);
    if (messages.length === 0) {
      setCurrentSuggestions(initialSuggestions);
    }
  };

  // Don't forget to clear the interval when the component unmounts
  useEffect(() => {
    return () => {
      if (loadingIntervalId) {
        clearInterval(loadingIntervalId);
      }
    };
  }, [loadingIntervalId]);

  const handleUndo = async () => {
    const undoPrompt = "Undo the last change and revert to the previous state.";
    setInput(undoPrompt);
    setCurrentLilyPondCode(previousLilyPondCode);
    
    // Log the undo event
    logEvent('Undo Action Performed', {
      previousCodeLength: previousLilyPondCode.length,
      messageCount: messages.length
    });

    await handleSubmit(null, undoPrompt);
    setIsUndoDialogOpen(false); // Close the dialog after undoing
  };

  const suggestionsRef = useRef(null);

  const checkScrollability = () => {
    if (suggestionsRef.current) {
      const { scrollLeft, scrollWidth, clientWidth } = suggestionsRef.current;
      setCanScrollLeft(scrollLeft > 0);
      setCanScrollRight(scrollLeft < scrollWidth - clientWidth);
    }
  };

  useEffect(() => {
    checkScrollability();
    window.addEventListener('resize', checkScrollability);
    return () => window.removeEventListener('resize', checkScrollability);
  }, [currentSuggestions]);

  const handleWheel = (e) => {
    if (e.deltaY !== 0 && suggestionsRef.current) {
      e.preventDefault();
      suggestionsRef.current.scrollLeft += e.deltaY;
      checkScrollability();
    }
  };

  const scrollSuggestions = (direction) => {
    if (suggestionsRef.current) {
      const scrollAmount = 200;
      suggestionsRef.current.scrollBy({
        left: direction === 'left' ? -scrollAmount : scrollAmount,
        behavior: 'smooth'
      });
      setTimeout(checkScrollability, 300); // Check after scroll animation
    }
  };

  const handleFileSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Check file size (e.g., 5MB limit)
    const MAX_FILE_SIZE = 1 * 1024 * 1024;
    if (file.size > MAX_FILE_SIZE) {
      alert('File size must be less than 1MB');
      logEvent('MIDI Upload Failed', { 
        reason: 'file_size_exceeded',
        fileSize: file.size,
        maxSize: MAX_FILE_SIZE
      });
      return;
    }

    // Check file type
    if (!file.type.includes('midi') && !file.type.includes('mid')) {
      alert('Only MIDI files are allowed');
      logEvent('MIDI Upload Failed', { 
        reason: 'invalid_file_type',
        fileType: file.type
      });
      return;
    }

    // Convert file to base64
    const reader = new FileReader();
    reader.onload = (event) => {
      const base64String = event.target.result.split(',')[1];
      setAttachedMidiFile({
        name: file.name,
        base64: base64String
      });
      logEvent('MIDI File Attached', {
        fileName: file.name,
        fileSize: file.size,
        isReplacement: !!attachedMidiFile // Track if this is a replacement
      });
    };
    reader.readAsDataURL(file);
  };

  // Add a reference for the file input
  const fileInputRef = useRef(null);

  // Add a click handler for the upload button
  const handleUploadClick = () => {
    logEvent('Upload MIDI Button Clicked', {
      action: attachedMidiFile ? 'replace' : 'new_upload'
    });
    fileInputRef.current?.click();
  };

  // Update the remove attachment handler
  const handleRemoveAttachment = () => {
    logEvent('MIDI File Removed', {
      fileName: attachedMidiFile?.name
    });
    setAttachedMidiFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleEmptyStateAction = async (suggestion, file = null) => {
    if (file) {
      // Handle file upload from EmptyState
      handleFileSelect({ target: { files: [file] } });
      // Scroll to input
      const inputElement = document.querySelector('input[type="text"]');
      if (inputElement) {
        inputElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    } else {
      // Use existing suggestion handler for text suggestions
      handleSuggestionClick(suggestion);
    }
  };

  return (
    <div className="h-full flex flex-col bg-transparent">
      <ScrollArea className="flex-grow overflow-y-auto" ref={scrollAreaRef}>
        <div className="max-w-1xl mx-auto px-4 py-6 space-y-6 h-full">
          {isLoadingHistory || isClearing ? (
            <div className="flex justify-center items-center h-full">
              <LoadingSpinner text={isClearing ? "Clearing chat history..." : "Loading chat history..."} />
            </div>
          ) : messages.length === 0 ? (
            <EmptyState 
              suggestions={initialSuggestions} 
              onSuggestionClick={handleEmptyStateAction}
            />
          ) : (
            messages.map((message, index) => (
              <React.Fragment key={index}>
                <div className={`flex ${message.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                  <div className={`flex items-start space-x-3 ${message.role === 'user' ? 'flex-row-reverse space-x-reverse' : 'flex-row'} max-w-[85%] sm:max-w-[75%] md:max-w-[600px]`}>
                    <div className={`flex-shrink-0 rounded-full p-2 ${message.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-muted'}`}>
                      {message.role === 'user' ? <User className="h-5 w-5" /> : <Bot className="h-5 w-5" />}
                    </div>
                    <div className={`rounded-lg p-4 ${message.role === 'user' ? 'bg-primary text-primary-foreground' : 'bg-gray-100'} overflow-hidden w-full`}>
                      <p className="break-words">{message.content || "No content"}</p>
                      {message.role === 'user' && message.midiAttachment && (
                        <div className="mt-2 flex items-center space-x-2 text-sm opacity-80">
                          <Upload className="h-3 w-3" />
                          <span>Attached: {message.midiAttachment}</span>
                        </div>
                      )}
                      {message.role === 'assistant' && message.filePath && (
                        <div className="mt-4 w-full max-w-[300px] sm:max-w-full mx-auto">
                          <MidiPlayer 
                            filePath={message.filePath} 
                            onPlay={() => logEvent('MIDI Playback Started', { filePath: message.filePath })}
                            onDownloaded={handleDownloaded}
                            onBeforeDownload={claimDownload}
                          />
                          {index === firstResultIndex && (
                            <p className="mt-2 text-xs text-muted-foreground">
                              Like it? Download the .mid and drag it into your DAW. Want changes? Pick a suggestion below or just tell me what to tweak.
                            </p>
                          )}
                          <div className="mt-2 flex flex-wrap items-center gap-1.5 text-xs text-muted-foreground">
                            {!ratings[index] ? (
                              <>
                                <span>Was this it?</span>
                                <button type="button" aria-label="Good result" className="rounded p-1 hover:bg-background hover:text-foreground" onClick={() => rateResult(index, 'up')}>
                                  <ThumbsUp className="h-3.5 w-3.5" />
                                </button>
                                <button type="button" aria-label="Bad result" className="rounded p-1 hover:bg-background hover:text-foreground" onClick={() => rateResult(index, 'down')}>
                                  <ThumbsDown className="h-3.5 w-3.5" />
                                </button>
                              </>
                            ) : ratings[index].rating === 'up' ? (
                              <span>Glad it works.</span>
                            ) : !ratings[index].reason ? (
                              <>
                                <span>What was off?</span>
                                {DOWN_REASONS.map(([key, label]) => (
                                  <button key={key} type="button" className="rounded-full border px-2 py-0.5 hover:bg-background hover:text-foreground" onClick={() => rateResult(index, 'down', key)}>
                                    {label}
                                  </button>
                                ))}
                              </>
                            ) : (
                              <span>Thanks. I drafted a redo below, edit it and send.</span>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </React.Fragment>
            ))
          )}
          {showPostDownloadOffer && (
            <div className="flex justify-start">
              <div className="flex items-start space-x-3 max-w-[85%] sm:max-w-[75%] md:max-w-[600px]">
                <div className="flex-shrink-0 rounded-full p-2 bg-muted">
                  <Sparkles className="h-5 w-5" />
                </div>
                <div className="rounded-lg p-4 bg-[#f05477]/10 border border-[#f05477]/20">
                  <p className="text-sm">
                    That one's in your DAW, on us. {credits === 1 ? 'You have 1 free token left' : `You have ${credits} free tokens left`} to keep shaping it; downloads after this first one come with
                    {trialEligible
                      ? ' the free trial (30 tokens, 7 days) or a one-off pack, which unlocks downloads for good with no subscription.'
                      : ' a one-off pack, which unlocks downloads for good with no subscription.'}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    {trialEligible && (
                      <Button size="sm" className="bg-[#f05477] hover:bg-[#d03357] text-white" onClick={() => handlePostDownloadOffer('start_trial')}>
                        Start 7-day free trial
                      </Button>
                    )}
                    <Button size="sm" variant={trialEligible ? 'outline' : 'default'} onClick={() => handlePostDownloadOffer('top_up')}>
                      {TOPUP_TOKENS} tokens for ${TOPUP_PRICE}
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handlePostDownloadOffer('dismiss')}>
                      Not now
                    </Button>
                  </div>
                  {trialEligible && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Trial: Musician plan, $9.99/month after 7 days, cancel anytime. Card needed for the trial.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          {showWallFeedback && (
            <WallFeedback user={user} source="download_wall" onDone={() => setShowWallFeedback(false)} />
          )}
          {showDownloadWall && (
            <div className="flex justify-start">
              <div className="flex items-start space-x-3 max-w-[85%] sm:max-w-[75%] md:max-w-[600px]">
                <div className="flex-shrink-0 rounded-full p-2 bg-muted">
                  <Lock className="h-5 w-5" />
                </div>
                <div className="rounded-lg p-4 bg-[#f05477]/10 border border-[#f05477]/20">
                  <p className="text-sm">
                    Your first download was free. To take this one too, grab the {TOPUP_TOKENS}-token pack for ${TOPUP_PRICE}: one-off, no subscription, and it unlocks downloads for good.
                    {trialEligible && ' Or start the 7-day free trial: 30 tokens, Pro mode and downloads included.'}
                  </p>
                  <div className="mt-3 flex flex-wrap gap-2">
                    <Button size="sm" className="bg-[#f05477] hover:bg-[#d03357] text-white" onClick={() => handleDownloadWall('top_up')}>
                      {TOPUP_TOKENS} tokens for ${TOPUP_PRICE}
                    </Button>
                    {trialEligible && (
                      <Button size="sm" variant="outline" onClick={() => handleDownloadWall('start_trial')}>
                        Start 7-day free trial
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" onClick={() => handleDownloadWall('dismiss')}>
                      Not now
                    </Button>
                  </div>
                  {trialEligible && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Trial: Musician plan, $9.99/month after 7 days, cancel anytime. Card needed for the trial.
                    </p>
                  )}
                </div>
              </div>
            </div>
          )}
          {isLoading && (
            <div className="flex justify-start">
              <div className="flex items-start space-x-3 max-w-[85%]">
                <div className="flex-shrink-0 rounded-full p-2 bg-muted">
                  <Bot className="h-5 w-5" />
                </div>
                <div className="rounded-lg p-4 bg-gray-100 overflow-hidden">
                  <LoadingSpinner text={`${loadingStatusFor(loadingElapsed)} ${loadingElapsed}s`} />
                  {mode === 'pro' && (
                    <p className="mt-2 text-xs text-muted-foreground">Pro mode usually takes 15–45 seconds for a full arrangement. Switch to Fast for quicker sketches.</p>
                  )}
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>
      </ScrollArea>
      
    

      <div className="bg-transparent">
        <div className="max-w-1xl mx-auto px-4 py-4">
            {/* Only show suggestions above chatbox when there are messages and not clearing history */}
      {messages.length > 0 && currentSuggestions.length > 0 && !isWaitingForNewSuggestions && !isClearing && (
        <div className="relative">
          <div className="absolute left-0 top-0 bottom-0 w-12 bg-gradient-to-r from-white to-transparent z-10"></div>
          <div className="absolute right-0 top-0 bottom-0 w-12 bg-gradient-to-l from-white to-transparent z-10"></div>
          <div 
            ref={suggestionsRef}
            className="overflow-x-auto scrollbar-thin scrollbar-thumb-gray-300 scrollbar-track-transparent px-12" 
            style={{ WebkitOverflowScrolling: 'touch' }}
            onWheel={handleWheel}
            onScroll={checkScrollability}
          >
            <div className="flex flex-nowrap gap-2 pb-2" style={{ minWidth: 'min-content' }}>
              {displayedSuggestions.map((suggestion, sugIndex) => (
                suggestion === "Undo" ? (
                  <Button
                    key={sugIndex}
                    onClick={() => handleSuggestionClick(suggestion)}
                    variant="outline"
                    size="sm"
                    className="text-xs sm:text-sm py-1 px-3 rounded-full text-black bg-white border-2 border-black hover:bg-black hover:text-white transition-colors duration-300 whitespace-nowrap flex-shrink-0 shadow-sm flex items-center space-x-1"
                  >
                    <Undo className="h-3 w-3" />
                    <span>{suggestion}</span>
                  </Button>
                ) : (
                  <Button
                    key={sugIndex}
                    onClick={() => handleSuggestionClick(suggestion)}
                    variant="outline"
                    size="sm"
                    className="text-xs sm:text-sm py-1 px-3 rounded-full text-[#f05477] bg-white border-2 border-[#f05477] hover:bg-[#f05477] hover:text-white transition-colors duration-300 whitespace-nowrap flex-shrink-0 shadow-sm"
                  >
                    {suggestion}
                  </Button>
                )
              ))}
            </div>
          </div>
          {canScrollLeft && (
            <button
              onClick={() => scrollSuggestions('left')}
              className="absolute left-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/80 rounded-full p-1.5 shadow-md z-20 transition-colors duration-200"
              aria-label="Scroll left"
            >
              <ChevronLeft className="h-5 w-5 text-white" />
            </button>
          )}
          {canScrollRight && (
            <button
              onClick={() => scrollSuggestions('right')}
              className="absolute right-2 top-1/2 transform -translate-y-1/2 bg-black/50 hover:bg-black/80 rounded-full p-1.5 shadow-md z-20 transition-colors duration-200"
              aria-label="Scroll right"
            >
              <ChevronRight className="h-5 w-5 text-white" />
            </button>
          )}
        </div>
      )}
          <div className="flex items-center space-x-2 mb-2">
            <div className="inline-flex rounded-md border border-input bg-background p-0.5" role="group" aria-label="Generation mode">
              <button
                type="button"
                onClick={() => handleModeChange('fast')}
                disabled={isLoading}
                aria-pressed={mode === 'fast'}
                className={`flex items-center space-x-1 rounded px-2.5 py-1 text-xs font-medium transition-colors ${mode === 'fast' ? 'bg-primary text-primary-foreground' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <Zap className="h-3 w-3" />
                <span>Fast</span>
              </button>
              <button
                type="button"
                onClick={() => handleModeChange('pro')}
                disabled={isLoading}
                aria-pressed={mode === 'pro'}
                className={`flex items-center space-x-1 rounded px-2.5 py-1 text-xs font-medium transition-colors ${mode === 'pro' ? 'bg-primary text-primary-foreground' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                {canUsePro ? <Sparkles className="h-3 w-3" /> : <Lock className="h-3 w-3" />}
                <span>Pro</span>
              </button>
            </div>
            <span className="text-xs text-gray-500">
              {mode === 'pro'
                ? 'Richer, fuller arrangements. Slower.'
                : canUsePro
                  ? 'Quick sketches in a few seconds.'
                  : 'Quick sketches in a few seconds. Pro mode comes with the Musician and Producer plans.'}
            </span>
          </div>
          <div className="flex space-x-2">
            <form onSubmit={(e) => handleSubmit(e)} className="flex space-x-2 flex-grow">
              <div className="relative flex-grow flex space-x-2">
                <div className="relative flex-grow">
                  <Input
                    ref={inputRef}
                    type="text"
                    value={input}
                    onChange={handleInputChange}
                    placeholder={
                      attachedMidiFile
                        ? "Enter your instructions..."
                        : windowWidth < 640 
                          ? "Type your desired chords, melody, etc..." 
                          : "Describe your desired chord progression, melody, modifications, etc."
                    }
                    disabled={isLoading}
                    className={`w-full ${attachedMidiFile ? 'pr-[200px]' : 'pr-16'} ${isInputFlashing ? 'input-flash' : ''} text-base sm:text-sm`}
                    onFocus={() => logEvent('Chat Input Focused')}
                    maxLength={MAX_CHARS}
                  />
                  <div className="absolute right-3 top-1/2 transform -translate-y-1/2 flex items-center space-x-3">
                    {attachedMidiFile && (
                      <div className="flex items-center space-x-2 border border-[#f05477]/20 bg-[#f05477]/5 rounded-md py-1.5 px-3 shadow-sm animate-in slide-in-from-right duration-300">
                        <div className="flex items-center space-x-2">
                          <div className="bg-[#f05477]/10 rounded-full p-1 animate-bounce">
                            <Upload className="h-3 w-3 text-[#f05477]" />
                          </div>
                          <span className="truncate max-w-[100px] text-xs font-medium text-gray-700">
                            {attachedMidiFile.name}
                          </span>
                        </div>
                        <button
                          type="button"
                          onClick={handleRemoveAttachment}
                          disabled={isLoading}
                          className={`rounded-full p-1 transition-colors ${
                            isLoading 
                              ? 'opacity-50 cursor-not-allowed' 
                              : 'hover:bg-[#f05477]/10'
                          }`}
                          aria-label="Remove attachment"
                        >
                          <X className="h-3 w-3 text-[#f05477]" />
                        </button>
                      </div>
                    )}
                    <span className="text-xs text-gray-400 flex-shrink-0">
                      {input.length}/{MAX_CHARS}
                    </span>
                  </div>
                </div>
                <label className="cursor-pointer flex-shrink-0">
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".mid,.midi"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    className={`px-2 py-2 flex items-center space-x-1 ${
                     'bg-primary text-primary-foreground' 
                    }`}
                    disabled={isLoading}
                    onClick={handleUploadClick}
                  >
                    <Upload className="h-4 w-4" />
                    <span className="hidden sm:inline text-sm">
                      {attachedMidiFile ? 'Replace MIDI' : 'Upload MIDI'}
                    </span>
                  </Button>
                </label>
              </div>
              <Button 
                type="submit" 
                disabled={isLoading || (!input.trim())}
                className="w-10 h-10 sm:w-auto sm:h-auto px-2 sm:px-4 py-2 text-sm font-medium text-white bg-[#f05477] hover:bg-[#d03357] rounded-md transition-colors duration-300 flex items-center justify-center sm:justify-start space-x-0 sm:space-x-2 shadow-sm"
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <>
                    <Send className="h-5 w-5" />
                    <span className="hidden sm:inline">Send</span>
                  </>
                )}
              </Button>
            </form>
            <Button 
              onClick={() => setIsClearConfirmOpen(true)}
              variant="outline"
              disabled={isClearing || !hasHistoryToClear}
              className="w-10 h-10 sm:w-auto sm:h-auto px-2 sm:px-4 py-2 text-sm font-medium text-black bg-white border-2 border-black rounded-md hover:bg-black hover:text-white transition-colors duration-300 flex items-center justify-center sm:justify-start space-x-0 sm:space-x-2 shadow-sm"
            >
              {isClearing ? (
                <Loader2 className="h-5 w-5 animate-spin" />
              ) : (
                <>
                  <RefreshCw className="h-5 w-5" />
                  <span className="hidden sm:inline">Clear</span>
                </>
              )}
            </Button>
          </div>
        </div>
      </div>

      <ClearHistoryDialog 
        isOpen={isClearConfirmOpen}
        onClose={() => setIsClearConfirmOpen(false)}
        onClear={handleClearHistory}
        isClearing={isClearing}
      />

      <OutOfCreditsDialog 
        isOpen={isOutOfCreditsDialogOpen}
        onClose={handleOutOfCreditsDialogClose}
        onPurchase={handlePurchaseCredits}
        onTopUp={() => handleTopUpPurchase(TOPUP_TOKENS)}
        isSubscriptionActive={isSubscriptionActive}
        trialEligible={trialEligible}
        hasTrack={messages.some((m) => m.role === 'assistant' && m.filePath)}
      />

      <UndoDialog 
        isOpen={isUndoDialogOpen}
        onClose={() => setIsUndoDialogOpen(false)}
        onUndo={handleUndo}
      />
    </div>
  );
}

export default Chat;